import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'components/form/formik';
import toast from 'react-hot-toast';
import { EyeIcon, EyeClosedIcon } from '@phosphor-icons/react';
import useSWR from 'swr';

import { Button } from '../../../components/button/Button';
import { PageHeader } from '../../../components/PageHeader';
import { getDisplayError } from '../../../utils/get-display-error';
import { InputField } from '../../../components/input/InputField';
import { useTranslation } from '../../../contexts/translation-context';
import { GoogleAuthButton } from '../components/GoogleAuthButton';
import { getMe, webPasswordLogin } from '../models';
import { SpinnerBlock } from '../../../components/Spinner';
import { trackEvent } from '../../../utils/track-event';

const loginSchema = Yup.object().shape({
  email: Yup.string().email().required('Vereist'),
  password: Yup.string().min(1, 'Vereist').required('Vereist'),
});

export const PortalLoginPage = () => {
  const navigate = useNavigate();
  const { data: me, isLoading } = useSWR('auth/me', getMe, {
    revalidateOnFocus: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (me) {
      navigate('/customer-portal', { replace: true });
    }
  }, [me, navigate]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <SpinnerBlock message="Loading..." />;
  }

  // eslint-disable-next-line no-restricted-globals
  const redirectUri = window.location.origin;
  return (
    <>
      <PageHeader title={i18n('auth.login.customerLoginTitle')} />

      <main className="p-8 min-h-screen w-full">
        <div className="flex flex-col justify-center h-full items-center mx-auto max-w-lg">
          <img className="w-64 mt-16 mb-8" src="/static/logo-dark.svg" />

          <div className="w-full">
            <Formik
              initialValues={{
                email: '',
                password: '',
              }}
              validationSchema={loginSchema}
              onSubmit={async (values) => {
                try {
                  const { email, password } = values;
                  const result = await webPasswordLogin(email, password);

                  const succeeded = result.success;
                  if (!succeeded) {
                    throw new Error(i18n('auth.login.incorrectCredentials'));
                  }

                  trackEvent('user_login_portal', { method: 'password' });

                  // This should trigger the redirect to the correct page
                  // Logins should invalidate all mem-caches
                  // eslint-disable-next-line no-restricted-globals
                  window.location.reload();
                } catch (err: any) {
                  toast.error(i18n('auth.login.loginFailed', { error: getDisplayError(err) }));
                }
              }}
            >
              {({ handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <div className="mb-2">
                    <InputField labelText={i18n('auth.login.email')} type="email" name="email" />
                  </div>

                  <div className="relative mb-6">
                    <div className="absolute flex text-sm underline text-fg-muted hover:text-brand-text justify-end right-0 mt-1">
                      <Link to="/forgot-password">{i18n('auth.login.forgotPassword')}</Link>
                    </div>

                    <InputField
                      labelText={i18n('auth.login.password')}
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                    />

                    {showPassword ? (
                      <EyeClosedIcon
                        className="absolute text-fg-muted hover:text-fg-secondary cursor-pointer w-6 h-6"
                        style={{ right: '.5rem', bottom: '2.1rem' }}
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    ) : (
                      <EyeIcon
                        className="absolute text-fg-muted hover:text-fg-secondary cursor-pointer w-6 h-6"
                        style={{ right: '.5rem', bottom: '2.1rem' }}
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    )}
                  </div>

                  <div>
                    <Button
                      type="submit"
                      color="primary"
                      width="w-full"
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                    >
                      {i18n('auth.login.login')}
                    </Button>
                  </div>
                </form>
              )}
            </Formik>
          </div>

          <div className="w-full flex items-center gap-4 my-8">
            <div className="w-full bg-border-strong select-none h-px">&nbsp;</div>
            <div className="text-fg-secondary">{i18n('or')}</div>
            <div className="w-full bg-border-strong select-none h-px">&nbsp;</div>
          </div>

          <div className="w-full flex">
            <GoogleAuthButton redirectUri={redirectUri} />
          </div>

          <div className="mt-4 w-full text-fg-secondary">
            <span className="mr-1">{i18n('portal.requestAccount.dontHaveAnAccount')}</span>
            <Link
              to="/request-customer-portal-account"
              className="mt-2 block underline font-medium hover:text-brand-text"
            >
              {i18n('portal.requestAccount.requestCustomerAccount')}
            </Link>
            <Link to="/request-carrier-portal-account" className="block  underline font-medium hover:text-brand-text">
              {i18n('portal.requestAccount.requestCarrierAccount')}
            </Link>
          </div>
        </div>
      </main>
    </>
  );
};
